import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, Sparkles, Plus, Minus, Info, Star } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const PERKS = {
  free:     ["300 documents · lifetime", "Basic features (Advanced Settings disabled)", "1 × Refill Pack permitted", "One PC · offline"],
  pro_1y:   ["14,999 documents per 12-month period", "All features enabled", "Unlimited Refill Packs while active", "One PC · offline"],
  lifetime: ["Unlimited documents · forever", "All features enabled", "No refills needed", "One PC · offline · lifetime updates"],
};

// Fallback in case backend doesn't return bulk_tiers
const FALLBACK_BULK_TIERS = {
  pro_1y: [
    [1, 4, 199],
    [5, 9, 179],
    [10, 24, 169],
    [25, 49, 159],
    [50, null, 149],
  ],
  lifetime: [
    [1, 4, 499],
    [5, 9, 449],
    [10, 24, 419],
    [25, 49, 399],
    [50, 99, 379],
  ],
};

function unitPriceFor(tiers, qty) {
  if (!tiers) return null;
  for (const t of tiers) {
    const lo = t[0], hi = t[1], price = t[2];
    if (qty >= lo && (hi === null || qty <= hi)) return price;
  }
  return null;
}

function QuantitySelector({ qty, setQty, planId, tiers, basePrice }) {
  const dec = () => setQty(Math.max(1, qty - 1));
  const inc = () => setQty(Math.min(99, qty + 1));
  const unit = unitPriceFor(tiers, qty) ?? basePrice;
  const total = unit * qty;
  const savedPerLicense = basePrice - unit;
  const totalSaved = savedPerLicense * qty;
  return (
    <div className="mt-5 border border-white/10 p-4 bg-black/40" data-testid={`bulk-selector-${planId}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400">Quantity</div>
        <div className="flex items-stretch border border-white/20">
          <button
            type="button"
            onClick={dec}
            disabled={qty <= 1}
            data-testid={`bulk-decrement-${planId}`}
            className="w-9 h-9 flex items-center justify-center text-white hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Decrease quantity"
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <div
            className="w-12 flex items-center justify-center font-mono text-white text-sm border-x border-white/20"
            data-testid={`bulk-quantity-${planId}`}
          >
            {qty}
          </div>
          <button
            type="button"
            onClick={inc}
            disabled={qty >= 99}
            data-testid={`bulk-increment-${planId}`}
            className="w-9 h-9 flex items-center justify-center text-white hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Increase quantity"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-neutral-500 uppercase tracking-[0.15em] text-[10px]">Unit price</div>
          <div className="text-white font-bold mt-0.5" data-testid={`bulk-unit-${planId}`}>${unit}</div>
        </div>
        <div className="text-right">
          <div className="text-neutral-500 uppercase tracking-[0.15em] text-[10px]">Total</div>
          <div className="text-[#F4A22A] font-bold mt-0.5" data-testid={`bulk-total-${planId}`}>${total}</div>
        </div>
      </div>

      {totalSaved > 0 && (
        <div
          className="mt-3 text-[11px] text-[#F4A22A] flex items-center gap-1.5"
          data-testid={`bulk-savings-${planId}`}
        >
          <Sparkles className="w-3 h-3" /> You save ${totalSaved} ({Math.round((savedPerLicense / basePrice) * 100)}% off per license)
        </div>
      )}

      {planId === "lifetime" && qty >= 99 && (
        <div className="mt-3 text-[11px] text-neutral-400">
          Need 100+? <Link to="/contact" className="text-[#F4A22A] underline">Contact for custom pricing</Link>
        </div>
      )}
    </div>
  );
}

function BulkTierTable({ planId, tiers, basePrice }) {
  if (!tiers) return null;
  const rows = tiers.map(([lo, hi, price]) => {
    const range = hi === null ? `${lo}+` : (lo === hi ? `${lo}` : `${lo}-${hi}`);
    const total = hi === null ? `$${price * lo}+` : `$${price * lo} - $${price * hi}`;
    const saved = basePrice - price;
    const savedPct = saved > 0 ? `${Math.round((saved / basePrice) * 100)}% off` : "—";
    return { range, price, total, saved, savedPct };
  });
  return (
    <div className="mt-6 overflow-x-auto" data-testid={`bulk-table-${planId}`}>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 border-b border-white/10">
            <th className="text-left py-3 pr-4 font-medium">Quantity</th>
            <th className="text-left py-3 pr-4 font-medium">Per license</th>
            <th className="text-left py-3 pr-4 font-medium">Range total</th>
            <th className="text-left py-3 font-medium">Discount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.range} className="border-b border-white/5 last:border-0">
              <td className="py-3 pr-4 text-white font-medium">{r.range}</td>
              <td className="py-3 pr-4 text-neutral-200 font-mono">${r.price}</td>
              <td className="py-3 pr-4 text-neutral-400 font-mono">{r.total}</td>
              <td className={`py-3 ${r.saved > 0 ? "text-[#F4A22A]" : "text-neutral-500"}`}>{r.savedPct}</td>
            </tr>
          ))}
          {planId === "lifetime" && (
            <tr className="border-b border-white/5 last:border-0">
              <td className="py-3 pr-4 text-white font-medium">100+</td>
              <td className="py-3 pr-4 text-neutral-300" colSpan={3}>
                <Link to="/contact" className="text-[#F4A22A] underline">Contact for custom pricing</Link>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function Pricing() {
  const [plans, setPlans] = useState([
  {
    id: "free",
    name: "Free Trial",
    tagline: "Smart freemium hook",
    price_usd: 0,
  },
  {
    id: "pro_1y",
    name: "Pro 1-Year",
    tagline: "≈ $0.013 / document · bulk discounts",
    price_usd: 199,
  },
  {
    id: "lifetime",
    name: "Lifetime",
    tagline: "Anti-SaaS · pay once · bulk discounts",
    price_usd: 499,
  },
  {
    id: "refill_1k",
    name: "Refill Pack",
    tagline: "1,500 Documents",
    price_usd: 10,
  },
]);
  const [bulkTiers, setBulkTiers] = useState(FALLBACK_BULK_TIERS);
  const [qtyPro, setQtyPro] = useState(1);
  const [qtyLife, setQtyLife] = useState(1);

  useEffect(() => {
    api.get("/plans").then(r => {
      setPlans(r.data.plans);
      if (r.data.bulk_tiers) setBulkTiers(r.data.bulk_tiers);
    }).catch(() => { /* keep default fallback plans on API failure */ });
  }, []);

  const mainPlans = useMemo(() => plans?.filter(p => p.id !== "refill_1k"), [plans]);
  const refill    = useMemo(() => plans?.find(p => p.id === "refill_1k"), [plans]);

  const qtyFor = (id) => id === "pro_1y" ? qtyPro : id === "lifetime" ? qtyLife : 1;

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24" data-testid="pricing-page">
      {/* Anti-subscription banner */}
      <div className="card-tile p-7 md:p-9 mb-14 max-w-4xl" data-testid="anti-subscription-banner">
        <div className="text-xs tracking-[0.25em] uppercase text-[#F4A22A] mb-3">No subscriptions</div>
        <div className="font-display font-bold text-2xl md:text-3xl text-white leading-tight">
          Your files don't need a subscription.
        </div>
        <p className="text-neutral-400 mt-4 text-sm md:text-base leading-relaxed">
          Techno Sort classifies and routes every PDF, DOCX, XLSX, and image on your machine —
          <span className="text-white"> offline, on-prem, one-time license</span>.
        </p>
      </div>

      <div className="max-w-3xl">
        <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-neutral-300 border border-white/15 px-3 py-1.5 bg-white/5">
          <Sparkles className="w-3 h-3 text-[#F4A22A]" />
          USD pricing · One PC · Offline
        </div>
        <h1 className="font-display font-bold text-4xl md:text-6xl text-white mt-6">
          Three plans. <span className="text-[#F4A22A]">Zero subscriptions.</span>
        </h1>
        <p className="text-neutral-400 mt-5 max-w-xl">
          Try the Free Trial first — 300 documents free, plus one optional Refill Pack of 1,500 docs at $10.
          That's <span className="text-white">1,800 documents of evaluation</span> before you commit a cent more.
          Pro and Lifetime plans include <span className="text-white">bulk-purchase discounts</span> — just bump the quantity.
        </p>
      </div>

      {/* MAIN 3 PLANS */}
      <div className="grid md:grid-cols-3 gap-5 mt-14 items-stretch">
        {!plans && Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-[520px] bg-white/5" />
        ))}
        {mainPlans?.map((p) => {
          const highlight = p.id === "lifetime";
          const valueProp = p.id === "free"
            ? "Smart freemium hook"
            : p.id === "pro_1y"
              ? "≈ $0.013 / document · bulk discounts"
              : "Anti-SaaS · pay once · bulk discounts";
          const qty = qtyFor(p.id);
          const tiers = bulkTiers[p.id];
          const showSelector = p.id === "pro_1y" || p.id === "lifetime";
          return (
            <div
              key={p.id}
              data-testid={`pricing-tier-${p.id}`}
              className={`card-tile p-8 flex flex-col relative ${highlight ? "metal-border" : ""}`}
            >
              {highlight && (
                <div
                  className="absolute -top-3 left-7 bg-[#F4A22A] text-black text-[10px] font-bold tracking-[0.2em] uppercase px-2 py-1 inline-flex items-center gap-1"
                  data-testid="most-popular-badge"
                >
                  <Star className="w-2.5 h-2.5 fill-black" /> Most Popular
                </div>
              )}
              <div className="text-xs tracking-[0.25em] uppercase text-neutral-500">{p.tagline}</div>
              <div className="font-display font-bold text-2xl text-white mt-2">{p.name}</div>

              <div className="mt-6 flex items-baseline gap-1">
                <span className="text-neutral-400 text-lg">$</span>
                <span className="font-display font-bold text-6xl text-white">{p.price_usd}</span>
                <span className="text-neutral-500 text-sm ml-1">USD</span>
              </div>
              <div className="text-neutral-500 text-xs mt-1">
                {p.id === "free" ? "free forever" : p.id === "pro_1y" ? "one-time · 12 months · per license" : "one-time · forever · per license"}
              </div>
              <div className="text-[11px] text-[#F4A22A] mt-2 uppercase tracking-[0.15em]">{valueProp}</div>

              <ul className="mt-7 space-y-3 text-sm text-neutral-300">
                {PERKS[p.id].map((f, i) => (
                  <li key={i} className="flex gap-2">
                    <Check className="w-4 h-4 text-[#F4A22A] mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>

              {showSelector && (
                <QuantitySelector
                  qty={qty}
                  setQty={p.id === "pro_1y" ? setQtyPro : setQtyLife}
                  planId={p.id}
                  tiers={tiers}
                  basePrice={p.price_usd}
                />
              )}

              <div className="flex-1" />

              {p.id === "free" ? (
                <Link
                  to="/download"
                  className="mt-8 border border-white/25 hover:bg-white/5 text-white py-3.5 px-4 text-xs font-bold uppercase tracking-wider text-center transition-colors"
                  data-testid={`pricing-cta-${p.id}`}
                >
                  Start Free Trial
                </Link>
              ) : (
                <Link
                  to={`/checkout/${p.id}?qty=${qty}`}
                  className={`mt-8 py-3.5 px-4 text-xs font-bold uppercase tracking-wider text-center ${highlight ? "btn-primary" : "bg-white text-black hover:bg-neutral-200 transition-colors"}`}
                  data-testid={`pricing-cta-${p.id}`}
                >
                  Buy {p.name}{qty > 1 ? ` × ${qty}` : ""}
                </Link>
              )}
            </div>
          );
        })}
      </div>

      {/* BULK PRICING TABLES */}
      <div className="mt-20 grid lg:grid-cols-2 gap-10" data-testid="bulk-pricing-section">
        <div className="card-tile p-7 md:p-8">
          <div className="text-xs tracking-[0.25em] uppercase text-neutral-400 mb-2">Bulk pricing</div>
          <h3 className="font-display font-bold text-2xl text-white">Pro 1-Year — volume discounts</h3>
          <p className="text-neutral-400 text-sm mt-2">Base $199 per license · 12-month term · per-machine binding.</p>
          <BulkTierTable planId="pro_1y" tiers={bulkTiers.pro_1y} basePrice={199} />
        </div>
        <div className="card-tile metal-border p-7 md:p-8">
          <div className="text-xs tracking-[0.25em] uppercase text-[#F4A22A] mb-2">Bulk pricing · Most Popular</div>
          <h3 className="font-display font-bold text-2xl text-white">Lifetime — volume discounts</h3>
          <p className="text-neutral-400 text-sm mt-2">Base $499 per license · pay once · per-machine binding.</p>
          <BulkTierTable planId="lifetime" tiers={bulkTiers.lifetime} basePrice={499} />
        </div>
      </div>

      {/* REFILL PACK */}
      {refill && (
        <div className="mt-16">
          <div className="text-xs tracking-[0.25em] uppercase text-neutral-400 mb-4">Add-on · Refill Pack</div>
          <div className="card-tile p-8 md:p-10 grid md:grid-cols-[1fr_auto] gap-8 items-center" data-testid="pricing-refill-card">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#F4A22A] mb-3">
                <Plus className="w-3 h-3" /> Refill Pack · 1,500 docs
              </div>
              <h3 className="font-display font-bold text-2xl md:text-3xl text-white">
                Top up 1,500 documents — power-user pressure relief valve.
              </h3>
              <p className="text-neutral-400 mt-3 max-w-2xl text-sm leading-relaxed">
                Hit your monthly quota? Stack 1,500 documents onto your existing license without re-installing or generating a new key.
                Credits don't expire while the underlying license is valid.
              </p>
            </div>
            <div className="flex flex-col items-start md:items-end gap-4">
              <div className="flex items-baseline gap-1">
                <span className="text-neutral-400 text-lg">$</span>
                <span className="font-display font-bold text-6xl text-white">{refill.price_usd}</span>
                <span className="text-neutral-500 text-sm ml-1">USD</span>
              </div>
              <Link
                to="/checkout/refill_1k"
                className="btn-primary px-7 py-3.5 text-xs font-bold uppercase tracking-wider"
                data-testid="pricing-cta-refill"
              >
                Buy Refill 1.5K
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="mt-12 text-xs text-neutral-500 tracking-wide flex items-start gap-2">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>
          * One license = one PC (hardware fingerprint). License transfers are at our sole discretion (one per 12 months) on written request to
          <a href="mailto:technosort.rd@gmail.com" className="text-neutral-300 underline ml-1">technosort.rd@gmail.com</a>.
          Supports Windows 10, Windows 11 and Windows Server 2019 – 2025 (64-bit).
        </span>
      </div>
    </section>
  );
}
